// AUTO-GENERATED by `php artisan reports:sync` — DO NOT EDIT BY HAND.
// Source of truth: backend/config/reports.php. One entry per report surface
// with the permission domain that opens it, whether the domain's `.own` key
// also opens it (narrowed to the caller), and the feature flags it needs
// (reports_enabled, the master switch, is listed first on every entry).
// Read through lib/reports.ts (reportAccess / useReportAccess); the sidebar
// and <ReportGuard> both resolve a report from this table so they cannot
// disagree. ReportRegistryTest pins this file to the backend registry.
export const REPORT_DOMAINS = ["finance", "sales", "clinical", "calls", "compensation"] as const;

export type ReportDomain = (typeof REPORT_DOMAINS)[number];

export interface ReportEntry {
  domain: ReportDomain;
  /** the domain's `.own` key also opens this report, narrowed to the caller */
  own: boolean;
  /** every flag must be on; `reports_enabled` first */
  flags: readonly string[];
  /** presentation gates: hide when any of these is ON */
  hideWhen: readonly string[];
  /** presentation gates: hide when any of these is explicitly OFF */
  hideWhenOff: readonly string[];
  path: string;
  title: string;
}

export const REPORTS = {
  "overview": {
    domain: "finance",
    own: false,
    flags: ["reports_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports",
    title: "Overview",
  },
  "business-category-pl": {
    domain: "finance",
    own: false,
    flags: ["reports_enabled", "business_categorization_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/business-category-pl/print",
    title: "Business-category P&L",
  },
  "profit-loss": {
    domain: "finance",
    own: false,
    flags: ["reports_enabled", "costs_enabled", "expense_engine_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/profit-loss",
    title: "P&L",
  },
  "debtors": {
    domain: "finance",
    own: false,
    flags: ["reports_enabled", "payments_enabled", "debtors_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/debtors",
    title: "Debtors",
  },
  "hotel-expenses": {
    domain: "finance",
    own: false,
    flags: ["reports_enabled", "hotel_expenses_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/hotel-expenses",
    title: "Hotel Expenses",
  },
  "supplier-payables": {
    domain: "finance",
    own: false,
    flags: ["reports_enabled", "purchase_order_payments_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/supplier-payables",
    title: "Supplier Payables",
  },
  "finance": {
    domain: "finance",
    own: false,
    flags: ["reports_enabled", "finance_dashboard_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/finance",
    title: "Finance",
  },
  "room-production": {
    domain: "finance",
    own: false,
    flags: ["reports_enabled", "rooms_enabled", "payments_enabled", "room_reports_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/room-production",
    title: "Room Production",
  },
  "doctor-production": {
    domain: "finance",
    own: true,
    flags: ["reports_enabled", "doctor_production_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/doctor-production",
    title: "Doctor Production",
  },
  "lead-analytics": {
    domain: "sales",
    own: false,
    flags: ["reports_enabled"],
    hideWhen: ["simple_clinic_mode"],
    hideWhenOff: ["lead_classification_enabled"],
    path: "/lead-analytics",
    title: "Lead Analytics",
  },
  "funnel": {
    domain: "sales",
    own: true,
    flags: ["reports_enabled", "funnel_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/funnel",
    title: "Funnel",
  },
  "team-performance": {
    domain: "sales",
    own: false,
    flags: ["reports_enabled", "team_performance_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/team-performance",
    title: "Team Performance",
  },
  "consultants": {
    domain: "sales",
    own: true,
    flags: ["reports_enabled", "consultant_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/consultants",
    title: "Consultants",
  },
  "follow-ups": {
    domain: "sales",
    own: true,
    flags: ["reports_enabled", "followup_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/follow-ups",
    title: "Follow-ups",
  },
  "visit-categorization": {
    domain: "sales",
    own: true,
    flags: ["reports_enabled", "visit_categorization_enabled", "visit_categorization_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/visit-categorization",
    title: "Visit Categorization",
  },
  "meta-funnel": {
    domain: "sales",
    own: false,
    flags: ["reports_enabled", "meta_marketing_reports_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/meta-funnel",
    title: "Meta Funnel",
  },
  "meta-speed-to-lead": {
    domain: "sales",
    own: false,
    flags: ["reports_enabled", "meta_marketing_reports_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/meta-speed-to-lead",
    title: "Meta Speed to Lead",
  },
  "meta-roas": {
    domain: "sales",
    own: false,
    flags: ["reports_enabled", "meta_marketing_reports_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/meta-roas",
    title: "Meta ROAS",
  },
  "meta-budget": {
    domain: "sales",
    own: false,
    flags: ["reports_enabled", "meta_marketing_reports_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/meta-budget",
    title: "Meta Budget",
  },
  "work-performed": {
    domain: "clinical",
    own: false,
    flags: ["reports_enabled", "work_performed_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/work-performed",
    title: "Work Performed",
  },
  "room-utilization": {
    domain: "clinical",
    own: false,
    flags: ["reports_enabled", "rooms_enabled", "room_reports_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/room-utilization",
    title: "Room Utilization",
  },
  "room-gaps": {
    domain: "clinical",
    own: false,
    flags: ["reports_enabled", "rooms_enabled", "room_reports_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/room-gaps",
    title: "Room Gaps",
  },
  "call-center": {
    domain: "calls",
    own: false,
    flags: ["reports_enabled", "call_center_reporting_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/call-center",
    title: "Call Reports",
  },
  "call-analytics": {
    domain: "calls",
    own: true,
    flags: ["reports_enabled", "telephony_enabled"],
    hideWhen: ["hide_telephony_pages_enabled"],
    hideWhenOff: [],
    path: "/call-analytics",
    title: "Call Analytics",
  },
  "doctor-commissions": {
    domain: "compensation",
    own: false,
    flags: ["reports_enabled", "doctor_service_commissions_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/doctor-commissions",
    title: "Doctor Commissions",
  },
  "affiliators": {
    domain: "compensation",
    own: false,
    flags: ["reports_enabled", "affiliator_report_enabled"],
    hideWhen: [],
    hideWhenOff: [],
    path: "/reports/affiliators",
    title: "Referral Partners",
  },
} as const satisfies Record<string, ReportEntry>;

export type ReportSlug = keyof typeof REPORTS;
